// The gateway owns identity, generated tasks and immutable designs. Shopify owns the cart.
import { prepareUploadFile, UploadObservation } from './ai-upload-observation.js'
const UPLOAD_REQUEST_TIMEOUT_MS = 60_000
const GCS_TRANSFER_TIMEOUT_MS = 120_000

export class GatewayClient {
  constructor(base) {
    const url = new URL(base)
    if (url.protocol !== 'https:' && !['localhost', '127.0.0.1'].includes(url.hostname)) {
      throw new Error('The Gateway URL must use HTTPS.')
    }
    this.base = url.origin
    this.uploadMode = 'disabled'
    this.uploadAttempts = new WeakMap()
  }

  async request(path, { body, method, key, signal, timeoutMs = 15_000, reauth = true, telemetry = this.analytics?.requestContext(), observation } = {}) {
    const multipart = body instanceof FormData
    let consented = false
    try { consented = telemetry?.context.analytics_consent === 'granted' && (this.analytics?.canTrack?.() ?? true) } catch {
      console.warn('Upload diagnostics consent unavailable; photo upload continues.')
    }
    const analyticsHeaders = consented ? { 'x-unicus-analytics': JSON.stringify(telemetry) } : {}
    const response = await fetch(`${this.base}/api/v1${path}`, {
      method: method || (body === undefined ? 'GET' : 'POST'),
      credentials: 'include',
      signal: signal ? AbortSignal.any([signal, AbortSignal.timeout(timeoutMs)]) : AbortSignal.timeout(timeoutMs),
      headers: { accept: 'application/json', ...analyticsHeaders, ...(!multipart && body !== undefined ? { 'content-type': 'application/json' } : {}), ...(key ? { 'idempotency-key': key } : {}) },
      ...(body === undefined ? {} : { body: multipart ? body : JSON.stringify(body) }),
    })
    if (observation) {
      observation.http_status = response.status
      const requestId = response.headers.get('X-Request-ID')
      observation.request_id = requestId && /^[\w-]{1,128}$/.test(requestId) ? requestId : null
    }
    const payload = await response.json().catch(() => null)
    if (response.status === 401 && reauth && path !== '/sessions') {
      try {
        await this.request('/sessions', { method: 'POST', reauth: false })
        return await this.request(path, { body, method, key, signal, timeoutMs, reauth: false, telemetry, observation })
      } catch { /* Surface the original failure below. */ }
    }
    if (!response.ok) {
      const error = new Error(payload?.error?.message || 'The Gateway request failed.')
      error.code = payload?.error?.code
      error.status = response.status
      const retryAfter = response.headers.get('retry-after')
      if (retryAfter && /^\d+$/.test(retryAfter)) error.retryAfterSeconds = Number(retryAfter)
      throw error
    }
    if (path === '/generations' && body !== undefined) this.observeMarketing('generationAccepted', payload)
    return payload
  }

  observeMarketing(method, ...args) {
    const warn = () => console.warn('AI marketing event unavailable; customization remains available.')
    try { Promise.resolve(this.marketing?.[method]?.(...args)).catch(warn) } catch { warn() }
  }

  async open() {
    await this.request('/sessions', { method: 'POST' })
    const capabilities = await this.request('/capabilities')
    if (!capabilities.generation) throw new Error('Generation is not enabled in this environment.')
    this.uploadMode = capabilities.uploadMode
    return capabilities
  }

  async prepareCart(design, selection) {
    const intent = { personIndexes: [...selection.personIndexes].sort((a, b) => a - b), manualEditing: selection.manualEditing,
      ...(selection.manualEditingPersonIndexes === undefined ? {} : { manualEditingPersonIndexes: [...selection.manualEditingPersonIndexes].sort((a, b) => a - b) }),
      sceneVariantId: selection.sceneVariantId ?? null, scenePhotoAssetId: selection.scenePhotoAssetId ?? null,
      sceneTemplateDigest: selection.sceneTemplateDigest ?? null }
    const signature = JSON.stringify({ id: design.id, ...intent })
    let attempt = this.cartPreparation
    if (!attempt || attempt.signature !== signature || ![attempt.body.expectedRevision, attempt.confirmedRevision].includes(design.revision)) {
      attempt = { signature, key: crypto.randomUUID(), body: { expectedRevision: design.revision, ...intent } }
      this.cartPreparation = attempt
    }
    // Retain the exact request on transport failure, including its original revision.
    const result = await this.request(`/customizations/${design.id}/prepare-cart`, { key: attempt.key, body: attempt.body })
    attempt.confirmedRevision = result.customization.revision
    this.observeMarketing('configurationConfirmed', result)
    return result
  }

  async upload(file, key, signal, purpose = 'person', preparedFile = file) {
    const observation = new UploadObservation(this.analytics, file, preparedFile)
    const run = () => this.performUpload(preparedFile, key, signal, purpose, observation)
    const asset = await (this.analytics ? this.analytics.upload(file, run, purpose) : run())
    this.observeMarketing('uploaded', asset, purpose)
    return asset
  }

  prepareUploadFile(file, loadCompression) {
    return prepareUploadFile(file, loadCompression, this.analytics)
  }

  async performUpload(file, key, signal, purpose, observation) {
    if (this.uploadMode === 'local') {
      const form = new FormData()
      form.set('file', file)
      form.set('purpose', purpose)
      const details = {}
      return observation.stage('multipart', async () => {
        const asset = await this.request('/assets', { body: form, key, signal, timeoutMs: UPLOAD_REQUEST_TIMEOUT_MS, telemetry: observation.telemetry, observation: details })
        details.asset_id = asset.id
        return asset
      }, details)
    }
    if (this.uploadMode !== 'gcs-direct') throw new Error('Photo upload is not configured.')
    let attempts = this.uploadAttempts.get(file)
    if (!attempts) { attempts = new Map(); this.uploadAttempts.set(file, attempts) }
    const attemptKey = `${purpose}:${key}`
    let attempt = attempts.get(attemptKey)
    if (!attempt) {
      const digest = await observation.stage('digest', async () => Array.from(new Uint8Array(await crypto.subtle.digest('SHA-256', await file.arrayBuffer())), (byte) => byte.toString(16).padStart(2, '0')).join(''))
      signal?.throwIfAborted()
      attempt = { key, digest }
      attempts.set(attemptKey, attempt)
    }
    observation.uploadId = attempt.id ?? null
    try {
      if (!attempt.uploaded) {
        const ticketDetails = {}
        const ticket = await observation.stage('ticket', async () => {
          const result = await this.request('/uploads', {
            key: attempt.key, signal, timeoutMs: UPLOAD_REQUEST_TIMEOUT_MS,
            body: { size: file.size, contentType: file.type, digest: attempt.digest, purpose },
            telemetry: observation.telemetry, observation: ticketDetails,
          })
          observation.uploadId = result.id
          if (result.asset) ticketDetails.asset_id = result.asset.id
          return result
        }, ticketDetails)
        if (ticket.asset) return ticket.asset
        attempt.id = ticket.id
        if (ticket.transfer) {
          try {
            const transferDetails = {}
            await observation.stage('transfer', async () => {
              const destination = new URL(ticket.transfer.url)
              if (destination.protocol !== 'https:' || destination.hostname !== 'storage.googleapis.com') {
                const error = new Error('Invalid photo storage destination.')
                error.code = 'UPLOAD_DESTINATION_INVALID'
                throw error
              }
              const form = new FormData()
              for (const [name, value] of Object.entries(ticket.transfer.fields)) form.set(name, value)
              form.set('file', file)
              const transferSignal = AbortSignal.any([signal, AbortSignal.timeout(GCS_TRANSFER_TIMEOUT_MS)].filter(Boolean))
              const response = await fetch(destination.href, { method: 'POST', credentials: 'omit', body: form, signal: transferSignal })
              transferDetails.http_status = response.status
              if (!response.ok) {
                const error = new Error('The photo upload could not finish. Please try again.')
                error.code = response.status === 413 ? 'PAYLOAD_TOO_LARGE' : 'UPLOAD_TRANSFER_FAILED'
                throw error
              }
            }, transferDetails)
          } catch (transferError) {
            this.releaseUpload(attempt.id)
            throw transferError
          }
        }
        attempt.uploaded = true
      }
      // Confirmation gets its own deadline after the potentially slow storage transfer.
      const confirmDetails = {}
      return await observation.stage('confirm', async () => {
        const asset = await this.request(`/uploads/${encodeURIComponent(attempt.id)}/complete`, { method: 'POST', signal, timeoutMs: UPLOAD_REQUEST_TIMEOUT_MS, telemetry: observation.telemetry, observation: confirmDetails })
        confirmDetails.asset_id = asset.id
        return asset
      }, confirmDetails)
    } catch (error) {
      if (['UPLOAD_EXPIRED', 'ASSET_EXPIRED'].includes(error.code)) {
        attempts.set(attemptKey, { key: crypto.randomUUID(), digest: attempt.digest })
      } else if (error.code === 'UPLOAD_NOT_READY') {
        attempt.uploaded = false
      }
      throw error
    }
  }

  async releaseUpload(id) {
    try {
      await this.request(`/uploads/${encodeURIComponent(id)}`, { method: 'DELETE', timeoutMs: 5_000 })
    } catch {}
  }

  async photoUrl(assetId) {
    const response = await fetch(`${this.base}/api/v1/assets/${encodeURIComponent(assetId)}`, { credentials: 'include', signal: AbortSignal.timeout(15_000) })
    if (!response.ok) return null
    return URL.createObjectURL(await response.blob())
  }
}

const MOCK_PREVIEW_PREFIX = 'unicus-mock-preview:'
const MOCK_PREVIEW_MAX_AGE_MS = 24 * 60 * 60 * 1000
const MOCK_PREVIEW_PENDING_ROWS = new WeakSet()
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

function previewOrigin(value) {
  const url = new URL(value)
  if (url.protocol !== 'https:' && !['localhost', '127.0.0.1'].includes(url.hostname)) {
    throw new Error('The mock preview Gateway URL must use HTTPS.')
  }
  return url.origin
}

export function rememberMockCartPreview(
  { customizationId, gatewayBase, assetId, figureVariantId, figureId },
  storage = globalThis.localStorage,
) {
  if (!UUID.test(customizationId) || !UUID.test(assetId) || (figureId && !UUID.test(figureId)) || !/^\d{1,20}$/.test(figureVariantId)) {
    throw new Error('The mock cart preview identifiers are invalid.')
  }
  storage.setItem(
    `${MOCK_PREVIEW_PREFIX}${customizationId}${figureId ? `:${figureId}` : ''}`,
    JSON.stringify({
      version: 1,
      gatewayOrigin: previewOrigin(gatewayBase),
      assetId,
      figureVariantId,
      savedAt: Date.now(),
    }),
  )
}

export function ensureCartPreviewImage(row) {
  const existing = row.querySelector('.cart-item__image')
  if (existing) return existing

  const media = row.querySelector('.cart-item__media')
  const document = media?.ownerDocument
  if (!media || !document) return null

  const container = document.createElement('div')
  container.className = 'cart-item__image-container gradient global-media-settings'
  const image = document.createElement('img')
  image.className = 'cart-item__image global-media-settings'
  image.loading = 'lazy'
  image.width = 150
  image.height = 150
  container.append(image)
  media.append(container)
  return image
}

export async function applyMockCartPreviews({
  root = globalThis.document,
  storage = globalThis.localStorage,
  fetcher = globalThis.fetch,
  createObjectURL = globalThis.URL.createObjectURL.bind(globalThis.URL),
} = {}) {
  if (!root || !storage || !fetcher || !createObjectURL) return 0
  let loaded = 0
  const rows = root.querySelectorAll('.cart-item[data-unicus-properties][data-variant-id]')
  for (const row of rows) {
    let properties
    try {
      const parsed = JSON.parse(row.dataset.unicusProperties || '{}')
      properties = Array.isArray(parsed) ? Object.fromEntries(parsed) : parsed
    } catch (error) {
      console.warn(
        'Mock cart preview properties are invalid.',
        error instanceof Error ? error.message : 'Unknown parsing error.',
      )
      continue
    }
    const customizationId = properties._unicus_customization
    if (properties._unicus_mock !== 'true' || !UUID.test(customizationId || '')) continue
    const figureId = properties._unicus_figure
    if (figureId && !UUID.test(figureId)) continue
    const key = `${MOCK_PREVIEW_PREFIX}${customizationId}${figureId ? `:${figureId}` : ''}`
    const serialized = storage.getItem(key)
    if (!serialized) continue
    try {
      const preview = JSON.parse(serialized)
      if (
        preview.version !== 1 ||
        !UUID.test(preview.assetId || '') ||
        !/^\d{1,20}$/.test(preview.figureVariantId || '') ||
        row.dataset.variantId !== String(preview.figureVariantId) ||
        !Number.isFinite(preview.savedAt) ||
        Date.now() - preview.savedAt > MOCK_PREVIEW_MAX_AGE_MS
      ) {
        if (Date.now() - preview.savedAt > MOCK_PREVIEW_MAX_AGE_MS) storage.removeItem(key)
        continue
      }
      if (
        row.dataset.unicusPreviewAssetId === preview.assetId ||
        MOCK_PREVIEW_PENDING_ROWS.has(row)
      )
        continue
      const origin = previewOrigin(preview.gatewayOrigin)
      MOCK_PREVIEW_PENDING_ROWS.add(row)
      try {
        const response = await fetcher(`${origin}/api/v1/assets/${preview.assetId}`, {
          credentials: 'include',
          signal: AbortSignal.timeout(15_000),
        })
        if (!response.ok || !response.headers.get('content-type')?.startsWith('image/')) continue
        const image = ensureCartPreviewImage(row)
        if (!image) continue
        image.removeAttribute('srcset')
        image.removeAttribute('sizes')
        image.src = createObjectURL(await response.blob())
        image.alt = 'Uploaded customization preview'
        row.dataset.unicusPreviewAssetId = preview.assetId
        loaded++
      } finally {
        MOCK_PREVIEW_PENDING_ROWS.delete(row)
      }
    } catch (error) {
      console.warn(
        'Mock cart preview is unavailable; keeping the Shopify product image.',
        error instanceof Error ? error.message : 'Unknown preview error.',
      )
    }
  }
  return loaded
}

export async function addHandoffToCart(handoff, { root = window.Shopify?.routes?.root || '/', storage = window.localStorage } = {}) {
  if (!navigator.locks) throw new Error('Use a browser supporting secure cart locking to continue.')
  return navigator.locks.request('unicus-cart-handoff', async () => {
    const request = async (path, body) => {
      const response = await fetch(`${root}${path}`, { method: body ? 'POST' : 'GET', credentials: 'same-origin', signal: AbortSignal.timeout(15_000), headers: { accept: 'application/json',  ...(body ? { 'content-type': 'application/json' } : {}) }, ...(body ? { body: JSON.stringify(body) } : {}) })
      const payload = await response.json()
      if (!response.ok) { const error = new Error(payload.description || payload.message || 'Shopify cart could not be updated.'); error.status = response.status; throw error }
      return payload
    }
    const key = `unicus-cart-pending:${handoff.id}`
    const matchesItem = (line, expected) => String(line.variant_id) === expected.id &&
      Object.entries(expected.properties).every(([name, value]) => line.properties?.[name] === value)
    const inspect = (cart) => {
      const lines = cart.items.filter((line) => line.properties?._unicus_handoff === handoff.id)
      if (!lines.length) return 'absent'
      const valid = handoff.items.every((expected) => {
        const matches = lines.filter((line) => matchesItem(line, expected))
        return matches.reduce((sum, line) => sum + line.quantity, 0) === expected.quantity
      }) && lines.every((line) => handoff.items.some((expected) => matchesItem(line, expected)))
      return valid ? 'complete' : 'conflict'
    }
    const cart = await request('cart.js')
    const state = inspect(cart)
    if (state === 'complete') { storage.removeItem(key); return cart }
    if (state === 'conflict' || storage.getItem(key)) throw new Error('A previous cart update is unconfirmed or differs from this design. Check your cart before trying again.')
    // Persist before sending. A network timeout is not proof that Shopify rejected the write.
    storage.setItem(key, 'pending')
    let failure
    try { await request('cart/add.js', { items: handoff.items }) } catch (error) { failure = error }
    const confirmed = await request('cart.js')
    if (inspect(confirmed) !== 'complete') throw failure || new Error('Cart update is not confirmed. Check your cart before trying again.')
    storage.removeItem(key)
    return confirmed
  })
}

if (typeof document !== 'undefined') {
  let observer
  let refreshPending = false
  const refreshMockCartPreviews = () => {
    if (refreshPending) return
    refreshPending = true
    queueMicrotask(() => {
      refreshPending = false
      void applyMockCartPreviews()
    })
  }
  const startMockCartPreviews = () => {
    refreshMockCartPreviews()
    observer?.disconnect()
    const cartRoot = document.querySelector('#main-cart-items')
    if (cartRoot && typeof MutationObserver !== 'undefined') {
      observer = new MutationObserver(refreshMockCartPreviews)
      observer.observe(cartRoot, { childList: true, subtree: true })
    }
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', startMockCartPreviews, { once: true })
  } else {
    startMockCartPreviews()
  }
  document.addEventListener('shopify:section:load', startMockCartPreviews)
}
